import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { WarehouseService } from 'src/app/services/warehouse.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { AssociationPriceListWarehouseService } from 'src/app/services/association-price-list-warehouse.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-association-price-list-warehouse',
  templateUrl: './association-price-list-warehouse.component.html',
  styleUrls: ['./association-price-list-warehouse.component.scss']
})
export class AssociationPriceListWarehouseComponent {
  loading = false;

  allWarehouses: any[] = [];
  allPriceLists: any[] = [];

  warehouses: any[] = [];
  priceLists: any[] = [];

  filterWarehouses = '';
  filterPriceLists = '';

  selectedWarehouseId: number | null = null;
  selectedWarehouse: any = null;
  initialRelatedPriceListIdsBefore: number[] = [];
  currentRelatedPriceListIds: number[] = [];
  currentRelatedPriceLists: any[] = [];

  paginationWarehouses = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationPriceLists = new SimplePaginationModel(0, 5, 0, 0, 0);


  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
  @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }

  private timeoutWarehouses: any = null;
  private timeoutPriceLists: any = null;

  constructor(
    private toastService: ToastrService,
    private warehouseService: WarehouseService,
    private inventoryService: InventoryService,
    private associationService: AssociationPriceListWarehouseService
  ) {}

  ngOnInit(): void {
    this.loadStartData();
  }

  loadStartData() {
    this.loadWarehouses();
    this.loadPriceLists();
  }

  loadWarehouses() {
    this.warehouseService.getWarehouses().subscribe({
      next: (resp: ResponseObject) => {
        this.allWarehouses = resp.data || [];
        this.refreshWarehousesPagination();
      },
      error: () => this.toastService.error('Error al obtener las bodegas')
    });
  }

  loadPriceLists() {
    this.inventoryService.getProductPricesList().subscribe({
      next: (resp: ResponseObject) => {
        this.allPriceLists = resp.data || [];
        this.refreshPriceListsPagination();
      },
      error: () => this.toastService.error('Error al obtener las listas de precios')
    });
  }

  selectWarehouse(warehouseId: number) {
    if (this.selectedWarehouseId === warehouseId) return;

    this.selectedWarehouseId = warehouseId;
    this.selectedWarehouse = this.allWarehouses.find(w => w.id === warehouseId);

    this.currentRelatedPriceListIds = [];
    this.currentRelatedPriceLists = [];

    this.associationService.getAssociateCompanies(warehouseId).subscribe({
      next: (resp: ResponseObject) => {
        const associations = resp.data || [];

        const relatedIds: number[] = associations.map((item: any) => item.priceListId);

        this.initialRelatedPriceListIdsBefore = [...relatedIds];
        this.currentRelatedPriceListIds = [...relatedIds];

        this.currentRelatedPriceLists = this.allPriceLists.filter(p =>
          relatedIds.includes(p.id)
        );

        this.refreshPriceListsPagination();
        this.drawMultipleLinks();
      },
      error: (error) => {
        console.error(error);
        this.toastService.error('Error al obtener asociaciones de la bodega');
      }
    });
  }

  togglePriceList(p: any) {
    if (!this.selectedWarehouseId) {
      this.toastService.warning('Selecciona primero una bodega.');
      return;
    }

    const idx = this.currentRelatedPriceListIds.indexOf(p.id);
    if (idx === -1) {
      this.currentRelatedPriceListIds.push(p.id);
    } else {
      this.currentRelatedPriceListIds.splice(idx, 1);
    }

    this.currentRelatedPriceLists = this.allPriceLists.filter(pl =>
      this.currentRelatedPriceListIds.includes(pl.id)
    );

    this.refreshPriceListsPagination();
    this.drawMultipleLinks();
  }

  getDisabledSave(): boolean {
    const initialSet = new Set(this.initialRelatedPriceListIdsBefore);
    const currentSet = new Set(this.currentRelatedPriceListIds);

    const toAdd = this.currentRelatedPriceListIds.some(id => !initialSet.has(id));
    const toRemove = this.initialRelatedPriceListIdsBefore.some(id => !currentSet.has(id));

    return !(toAdd || toRemove) || !this.selectedWarehouseId || this.loading;
  }

  saveAssociation(finalize: boolean) {
    if (!this.selectedWarehouseId) return;

    const warehouseId = this.selectedWarehouseId;

    const initialSet = new Set(this.initialRelatedPriceListIdsBefore);
    const currentSet = new Set(this.currentRelatedPriceListIds);

    const toAdd = this.currentRelatedPriceListIds.filter(id => !initialSet.has(id));
    const toRemove = this.initialRelatedPriceListIdsBefore.filter(id => !currentSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.toastService.info('No hay cambios por guardar.');
      return;
    }

    this.loading = true;

    const ops = [
      ...toAdd.map(pid => this.associationService.createRelationWarehousePriceList(warehouseId, pid)),
      ...toRemove.map(pid => this.associationService.deleteRelationWarehousePriceList(warehouseId, pid))
    ];

    forkJoin(ops).subscribe({
      next: () => {
        this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
        this.loading = false;
        this.selectWarehouse(warehouseId);
        if (finalize) this.toastService.info('Asociación finalizada correctamente.');
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar asociaciones', 'Error');
        this.loading = false;
      }
    });
  }

  debouncedFilterWarehouses() {
    clearTimeout(this.timeoutWarehouses);
    this.timeoutWarehouses = setTimeout(() => {
      this.paginationWarehouses.page = 0;
      this.refreshWarehousesPagination();
    }, 250);
  }

  refreshWarehousesPagination() {
    const q = (this.filterWarehouses || '').toLowerCase();
    const filtered = this.allWarehouses.filter(w =>
      (w.name || '').toLowerCase().includes(q) ||
      (w.code || '').toLowerCase().includes(q)
    );

    this.paginationWarehouses.total = filtered.length;
    this.paginationWarehouses.start = this.paginationWarehouses.page * this.paginationWarehouses.size;
    const end = this.paginationWarehouses.start + this.paginationWarehouses.size;
    this.paginationWarehouses.end = Math.min(end, filtered.length);

    this.warehouses = filtered.slice(this.paginationWarehouses.start, this.paginationWarehouses.end);
    this.drawMultipleLinks();
  }

  debouncedFilterPriceLists() {
    clearTimeout(this.timeoutPriceLists);
    this.timeoutPriceLists = setTimeout(() => {
      this.paginationPriceLists.page = 0;
      this.refreshPriceListsPagination();
    }, 250);
  }

  refreshPriceListsPagination() {
    const q = (this.filterPriceLists || '').toLowerCase();
    const matches = (p: any) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q);

    const associatedSet = new Set(this.currentRelatedPriceListIds);
    const pinned = this.allPriceLists.filter(p => associatedSet.has(p.id));
    const rest = this.allPriceLists.filter(p => !associatedSet.has(p.id) && matches(p));

    const final = [...pinned, ...rest];

    this.paginationPriceLists.total = final.length;
    this.paginationPriceLists.start = this.paginationPriceLists.page * this.paginationPriceLists.size;
    const end = this.paginationPriceLists.start + this.paginationPriceLists.size;
    this.paginationPriceLists.end = Math.min(end, final.length);

    this.priceLists = final.slice(this.paginationPriceLists.start, this.paginationPriceLists.end);
    this.drawMultipleLinks();
  }

  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';

    if (!this.selectedWarehouseId || this.currentRelatedPriceListIds.length === 0) return;

    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-warehouses tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      for (const pid of this.currentRelatedPriceListIds) {
        const relatedRow = document.querySelector(`#table-pricelists tr[data-id="${pid}"]`) as HTMLElement;
        if (!relatedRow) continue;

        const rect = relatedRow.getBoundingClientRect();
        const x2 = rect.left - parentRect.left - 12;
        const y2 = rect.top + rect.height / 2 - parentRect.top;
        const xMiddle = x1 + ((x2 - x1) / 2);

        this.drawLine(svg, x1, xMiddle, y1, y1);
        this.drawLine(svg, xMiddle, x2, y2, y2);
        this.drawLine(svg, xMiddle, xMiddle, y1, y2);
      }
    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#14532d');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}
